




























import { defineComponent, ref, PropType, computed, useContext } from '@nuxtjs/composition-api';
import dayjs from 'dayjs';
import ProductDetailAboutEvaluationModalBody from '~/components/pages/Product/Detail/ProductDetailAboutEvaluationModalBody.vue';
import ProductDetailStockCard from '~/components/pages/Product/Detail/ProductDetailStockCard.vue';
import AppIcon from '~/components/partials/App/AppIcon.vue';
import AppModal from '~/components/partials/App/AppModal.vue';
import { useMatchMedia } from '~/composables/useMatchMedia';
import { useStore } from '~/composables/useStore';
import { STOCK_SECTION_ANCHOR_POINT } from '~/constants';
import { formatWhenToDrink, getLotteryInfoFromEC } from '~/utils';
import {
  formatKeyValueArray,
  getSpecialPricePromotion,
  getProductPrice,
  parseReviewMediumInfo,
} from '~/utils/modules/products';
import { ProductShowECResponseSchema, ProductStockCard } from '~/types/modules/product';
import { PromotionsName } from '~/enums';

export default defineComponent({
  components: {
    AppIcon,
    AppModal,
    ProductDetailAboutEvaluationModalBody,
    ProductDetailStockCard,
  },
  props: {
    ecResponse: {
      type: Object as PropType<ProductShowECResponseSchema>,
      required: true,
    },
  },
  setup(props) {
    const { isMobile } = useMatchMedia();
    const { i18n } = useContext();
    const store = useStore();
    const isOpen = ref(false);
    const isVisaMember = computed(() => {
      if (!store.state.auth.authenticated) {
        return false;
      }
      return store.getters['user/info/isVisaMember'];
    });

    const stockList = computed((): ProductStockCard[] => {
      const productVariants = props.ecResponse.product_variants.filter((variant) => variant.id);
      if (!productVariants.length) {
        return [];
      }
      return productVariants.map((variant) => {
        const formattedAttributes = formatKeyValueArray(variant.attributes);
        const SpecialPricePromotion = getSpecialPricePromotion(variant.price_settings);

        return {
          productVariantId: Number(variant.id),
          isFreeShipping: variant.price_settings.is_free_shipping,
          discountRate: SpecialPricePromotion?.discount_rate,
          stock: variant.stock_count || 0,
          catchCopy: formattedAttributes?.catchcopy,
          saleLabel: variant.price_settings.sale_info,
          price: getProductPrice(variant.price_settings),
          vintageCode: variant.vintage_code,
          reviewMediumInfos: parseReviewMediumInfo(variant.review_medium_info),
          isLimitedStock: formattedAttributes?.mark_limited_stock_flag === 'true',
          isOnlineOnly: props.ecResponse.is_online_only,
          isOnlineCampaign: formattedAttributes?.mark_limited_online_campaign_flag === 'true',
          vintageInfo: variant.vintage_description,
          vintageInfo2: variant.vintage_info_2,
          tasteReview: variant.taste_review,
          whenToDrink: formatWhenToDrink(variant.when_to_drink),
          etiquetteImage: variant.etiquette_image,
          howAging: variant.how_aging,
          alcoholDegree: variant.alcohol_degree,
          awardInformation: variant.award_information,
          stopper: formattedAttributes?.stopper,
          limitedStockCount: variant.sales_quantity_limit,
          isDisabledToSetShippingOption: variant.shipping_setting_not_allowed_flag,
          isNowOnSale: variant.now_on_sale_flag,
          sameProductPromotionQuantity: variant.single_product_promotions?.length
            ? Math.min(...variant.single_product_promotions.map((promotion) => promotion.quantity))
            : 0,
          sameProductPromotionNames: variant.single_product_promotions.map(({ name }) => name) || [],
          multiProductPromotionQuantity: variant.multi_product_promotions?.length
            ? Math.min(...variant.multi_product_promotions.map((promotion) => promotion.quantity))
            : 0,
          multiProductPromotionNames: variant.multi_product_promotions.map(({ name }) => name) || [],
          hasVisaHappyWine:
            isVisaMember.value &&
            variant.price_settings.promotions.some(
              (promotion) => promotion.name === PromotionsName.VISA_HAPPY_WINE_PRICE
            ),
          brands: variant.brands?.map(({ name, rate }) => ({ name, rate })) || [],
          earliestShippingDate:
            variant.earliest_shipping_date && dayjs(variant.earliest_shipping_date).isValid()
              ? dayjs(variant.earliest_shipping_date).tz('Asia/Tokyo').format('MMMMDo')
              : '',
          isD1Target: variant.d1_icon_display_flag,
          lottery: {
            ...getLotteryInfoFromEC(variant.raffle_item?.raffle),
            text: i18n.t('Item-03.button.withinLotteryPeriod.type4').toString(),
          },
        };
      });
    });

    return {
      isOpen,
      stockList,
      isMobile,
      STOCK_SECTION_ANCHOR_POINT,
    };
  },
});
